import { ColumnDef } from '@tanstack/react-table';
import { CheckSquare, Copy, Eye, MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { ConfirmDialog, MessageDialog } from '../components/ui/alert-dialog-helper';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { createDateColumn } from '../components/ui/column-helpers';
import { DataTable } from '../components/ui/data-table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { TaskTemplate, TaskType } from '../types/task';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: Array<{ key: string; name: string; description: string }>;
}

const TaskTemplates: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [templates, setTemplates] = useState<TaskTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | TaskType>('all');

  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<TaskTemplate | null>(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);

      const isSupervisorOrAdmin = parsedUser.roles?.some((role: any) =>
        ['admin', 'supervisor'].includes(role.key)
      );

      if (!isSupervisorOrAdmin) {
        navigate('/');
        return;
      }
    }
    loadTemplates();
  }, [navigate]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/tasks/templates', {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();
      if (data.success) {
        setTemplates(data.data);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!templateToDelete) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/tasks/templates/${templateToDelete.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();
      if (data.success) {
        await loadTemplates();
      } else {
        setErrorMessage(data.message || 'Failed to delete template');
        setErrorDialogOpen(true);
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      setErrorMessage('An error occurred while deleting the template');
      setErrorDialogOpen(true);
    } finally {
      setTemplateToDelete(null);
    }
  };

  const handleDuplicate = async (template: TaskTemplate) => {
    const newName = prompt(`Enter name for duplicated template:`, `${template.name} (Copy)`);
    if (!newName) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/tasks/templates/${template.id}/duplicate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ new_name: newName }),
      });

      const data = await response.json();
      if (data.success) {
        await loadTemplates();
      } else {
        setErrorMessage(data.message || 'Failed to duplicate template');
        setErrorDialogOpen(true);
      }
    } catch (error) {
      console.error('Error duplicating template:', error);
      setErrorMessage('An error occurred while duplicating the template');
      setErrorDialogOpen(true);
    }
  };

  const formatTaskType = (type: string) => {
    return type
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getPriorityBadge = (priority: string) => {
    const priorityMap: Record<string, 'default' | 'warning' | 'destructive' | 'secondary'> = {
      urgent: 'destructive',
      high: 'warning',
      normal: 'default',
      low: 'secondary',
    };
    return priorityMap[priority] || 'default';
  };

  const filteredTemplates = filter === 'all'
    ? templates
    : templates.filter(t => t.task_type === filter);

  const columns: ColumnDef<TaskTemplate>[] = [
    {
      accessorKey: 'name',
      header: 'Template Name',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <CheckSquare className="h-4 w-4 text-muted-foreground" />
          <button
            onClick={() => navigate(`/task-templates/${row.original.id}/edit`)}
            className="font-medium text-gray-900 hover:text-gray-700 underline hover:no-underline"
          >
            {row.original.name}
          </button>
        </div>
      ),
    },
    {
      accessorKey: 'task_type',
      header: 'Task Type',
      cell: ({ row }) => formatTaskType(row.original.task_type),
    },
    {
      accessorKey: 'default_priority',
      header: 'Priority',
      cell: ({ row }) => (
        <Badge variant={getPriorityBadge(row.original.default_priority)}>
          {row.original.default_priority.toUpperCase()}
        </Badge>
      ),
    },
    {
      accessorKey: 'template_items',
      header: 'Checklist Items',
      cell: ({ row }) => (
        <div className="text-center">{row.original.template_items?.length || 0}</div>
      ),
    },
    {
      accessorKey: 'estimated_duration_hours',
      header: 'Duration',
      cell: ({ row }) => {
        const hours = row.original.estimated_duration_hours;
        return hours ? `${hours}h` : '-';
      },
    },
    {
      accessorKey: 'requires_photo',
      header: 'Photo Required',
      cell: ({ row }) => (
        <Badge variant={row.original.requires_photo ? 'default' : 'secondary'}>
          {row.original.requires_photo ? 'Yes' : 'No'}
        </Badge>
      ),
    },
    {
      accessorKey: 'is_active',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={row.original.is_active ? 'default' : 'secondary'}>
          {row.original.is_active ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    createDateColumn<TaskTemplate>('created_at', 'Created At', 'datetime'),
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const template = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate(`/task-templates/${template.id}/edit`)}>
                <Eye className="h-4 w-4 mr-2" />
                View
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/task-templates/${template.id}/edit`)}>
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleDuplicate(template)}>
                <Copy className="h-4 w-4 mr-2" />
                Duplicate
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-red-600"
                onClick={() => {
                  setTemplateToDelete(template);
                  setDeleteDialogOpen(true);
                }}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} currentPage="task-templates" />
      <main className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-primary font-heading">Task Templates</h1>
            <p className="text-muted-foreground mt-1">
              Manage reusable task templates with predefined checklists
            </p>
          </div>
          <Button onClick={() => navigate('/task-templates/create')}>
            <Plus className="h-4 w-4 mr-2" />
            Create Template
          </Button>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('all')}
          >
            All ({templates.length})
          </Button>
          {Object.values(TaskType).map((type) => {
            const count = templates.filter(t => t.task_type === type).length;
            if (count === 0) return null;
            return (
              <Button
                key={type}
                variant={filter === type ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter(type)}
              >
                {formatTaskType(type)} ({count})
              </Button>
            );
          })}
        </div>

        {/* Templates Table */}
        <DataTable
          columns={columns}
          data={filteredTemplates}
          loading={loading}
          searchKey="name"
          searchPlaceholder="Search templates..."
          emptyMessage="No templates found"
        />
      </main>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Template"
        description={`Are you sure you want to delete template "${templateToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        variant="destructive"
      />

      {/* Error Message Dialog */}
      <MessageDialog
        open={errorDialogOpen}
        onOpenChange={setErrorDialogOpen}
        title="Error"
        description={errorMessage}
        variant="destructive"
      />
    </div>
  );
};

export default TaskTemplates;
